import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AppLayout from '../screens/AppLayout';
import GoldenThemeAppLayout from '../screens/GoldenThemeAppLayout';
import GreyAppLayout from '../screens/GreyAppLayout';
import Loader from '../components/loader/Loader';

// Route-level code splitting: each screen is its own chunk, so a visitor downloads only the
// screen they open (e.g. /plan/subscribe, /challenge-onboarding) instead of all 44 screens.
// The JSX below is unchanged: lazyPage returns a component that renders the screen in Suspense.
// After a deploy, a tab opened before it still points at the old chunk files, which no longer
// exist; reload once to pick up the new build instead of showing a broken page.
const RELOAD_FLAG = 'chunk-reload-at';
const loadWithReload = (load) => () =>
	load().catch((error) => {
		const isChunkError = /ChunkLoadError|Loading chunk|Loading CSS chunk/i.test(
			`${error?.name} ${error?.message}`
		);
		let last = 0;
		try {
			last = Number(sessionStorage.getItem(RELOAD_FLAG)) || 0;
		} catch (e) {}
		if (isChunkError && Date.now() - last > 60 * 1000) {
			try {
				sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
			} catch (e) {}
			window.location.reload();
			return new Promise(() => {}); // keep the fallback on screen while the page reloads
		}
		throw error;
	});

const lazyPage = (load) => {
	const Screen = lazy(loadWithReload(load));
	const Page = (props) => (
		<Suspense fallback={<Loader grey />}>
			<Screen {...props} />
		</Suspense>
	);
	return Page;
};

const App = lazyPage(() => import('../App'));
const Login = lazyPage(() => import('../screens/login/Login'));
const SignUp = lazyPage(() => import('../screens/signup-form/SignUp'));
const LandingPageOne = lazyPage(() => import('../screens/LandingPageOne'));
const KarmaPoints = lazyPage(() => import('../screens/karma-points/KarmaPoints'));
const Leaderboard = lazyPage(() => import('../screens/leaderboard/Leaderboard'));
const Referral = lazyPage(() => import('../screens/referral/Referral'));
const ReferralHistory = lazyPage(() => import('../screens/referral/ReferralHistory'));
const Founders = lazyPage(() => import('../screens/founders/Founders'));
const SampleClasses = lazyPage(() => import('../screens/sample-classes/SampleClasses'));
const AccountCreated = lazyPage(() => import('../screens/home/components/AccountCreated'));
const OnBoarding = lazyPage(() => import('../screens/signup-form/OnBoarding'));
const UpcomingSessions = lazyPage(() => import('../screens/live-class/UpcomingSessions'));
const HowToJoinLiveClass = lazyPage(() => import('../screens/HowToJoinLiveClass'));
const ChangeTimeSlotsPage = lazyPage(() => import('../screens/signup-form/ChangeTimeSlots'));
const ManageReminders = lazyPage(() => import('../screens/live-class/ManageReminders'));
const VimeoVideoPlayer = lazyPage(() => import('../screens/plans/components/VimeoVideoPlayer'));
const UpcomingCourseDescriptionPage = lazyPage(() => import('../screens/course/UpcomingCourseDescriptionPage'));
const SeekCommunity = lazyPage(() => import('../screens/home/components/SeekCommunity'));
const UpcomingCourseClass = lazyPage(() => import('../screens/course/UpcommingCourseClass'));
const CourseHomework = lazyPage(() => import('../screens/course/CourseHomework'));
const HomeContainer = lazyPage(() => import('../screens/home-v2/HomeContainer'));
const FavouriteClasses = lazyPage(() => import('../screens/sample-classes/FavouriteClasses'));
const PostOBScreen = lazyPage(() => import('../screens/home/components/PostOBScreen'));
const CompleteProfile = lazyPage(() => import('../screens/home/components/CompleteProfile'));
const PocLandingPage = lazyPage(() => import('../screens/poc-counselling/PocLandingPage'));
const PlanPaymentSuccess = lazyPage(() => import('../screens/plans/PaymentSuccessScreen'));
const ManageNotification = lazyPage(() => import('../screens/live-class/ManageNotification'));
const PrivacyPolicy = lazyPage(() => import('../screens/profile/PrivacyPolicy'));
const TermsConditions = lazyPage(() => import('../screens/profile/TermsConditions'));
const Support = lazyPage(() => import('../screens/profile/Support'));
const PlanScreen = lazyPage(() => import('../screens/plans/PlanScreen'));
const CoursePaymentSuccessScreen = lazyPage(() => import('../screens/course/components/CoursePaymentSuccessScreen'));
const SaiServer = lazyPage(() => import('../screens/profile/SaiServer'));
const SaiElevenLabsCall = lazyPage(() => import('../screens/profile/SaiElevenLabsCall'));
const UpcomingEventDescriptionPage = lazyPage(() => import('../screens/course/UpcomingEventDescriptionPage'));
const EventPaymentSuccessScreen = lazyPage(() => import('../screens/course/components/EventPaymentSuccessScreen'));
const ChallengeOnboarding = lazyPage(() => import('../screens/challenge-ob/ChallengeOnboarding'));
const SaiElevenLabsCallTest = lazyPage(() => import('../screens/profile/SaiElevenLabsCallTest'));
const SaiCallAndChat = lazyPage(() => import('../screens/profile/SaiCallAndChat'));

const userString = window.localStorage.getItem('user');

const user = JSON.parse(userString);

const ProtectedRoute = ({ element }) => {
	if (user) {
		return element;
	} else {
		return <Navigate to='/login' />;
	}
};

export const router = createBrowserRouter([
	{
		path: '/',
		element: (
			<AppLayout>
				<App />
			</AppLayout>
		),
	},
	{
		path: '/landing-page-one',
		element: (
			<AppLayout>
				<LandingPageOne />
			</AppLayout>
		),
	},
	{
		path: '/login',
		element: (
			<GreyAppLayout>
				<Login />
			</GreyAppLayout>
		),
	},
	{
		path: '/onboarding',
		element: (
			<GoldenThemeAppLayout>
				<OnBoarding />
			</GoldenThemeAppLayout>
		),
	},
	{
		path: '/challenge-onboarding',
		element: (
			<AppLayout>
				<ChallengeOnboarding />
			</AppLayout>
		),
	},
	{
		path: '/sign-up-successful',
		element: (
			<GoldenThemeAppLayout>
				<AccountCreated />
			</GoldenThemeAppLayout>
		),
	},
	{
		path: '/seek-counselling',
		element: (
			<AppLayout>
				<PocLandingPage />
			</AppLayout>
		),
	},
	{
		path: '/get-started',
		element: (
			<GoldenThemeAppLayout>
				<PostOBScreen />
			</GoldenThemeAppLayout>
		),
	},

	{
		path: '/complete-your-profile',
		element: (
			<GreyAppLayout>
				<CompleteProfile />
			</GreyAppLayout>
		),
	},

	{
		path: '/sign-up',
		element: (
			<GreyAppLayout>
				<SignUp />
			</GreyAppLayout>
		),
	},
	{
		path: '/home',
		element: (
			<AppLayout>
				<HomeContainer />
			</AppLayout>
		),
	},
	{
		path: '/live-classes',
		element: (
			<AppLayout>
				<HomeContainer />
			</AppLayout>
		),
	},
	{
		path: '/profile',
		element: (
			<AppLayout>
				<HomeContainer />
			</AppLayout>
		),
	},
	{
		path: '/courses',
		element: (
			<AppLayout>
				<HomeContainer />
			</AppLayout>
		),
	},
	{
		path: '/karma',
		element: (
			<AppLayout>
				<KarmaPoints key={'KarmaPoints'} />
			</AppLayout>
		),
	},
	{
		path: '/leaderboard',
		element: (
			<AppLayout>
				<Leaderboard key={'Leaderboard'} />
			</AppLayout>
		),
	},
	{
		path: '/referral',
		element: (
			<AppLayout>
				<Referral key={'Referral'} />
			</AppLayout>
		),
	},
	{
		path: '/referralHistory',
		element: (
			<AppLayout>
				<ReferralHistory key={'ReferralHistory'} />
			</AppLayout>
		),
	},
	{
		path: '/plan',
		element: (
			<AppLayout>
				<PlanScreen key={'PlanScreen'} />
			</AppLayout>
		),
	},
	{
		path: '/plan/subscribe',
		element: (
			<AppLayout>
				<PlanScreen key={'PlanScreen'} subscribe={true} />
			</AppLayout>
		),
	},
	{
		path: '/plan/one',
		element: (
			<AppLayout>
				<PlanScreen key={'PlanScreen'} onetime={true} />
			</AppLayout>
		),
	},
	{
		path: '/plan/one/payment-successful',
		element: (
			<AppLayout>
				<PlanPaymentSuccess onetime key={'PlanOnePaymentSuccess'} />
			</AppLayout>
		),
	},
	{
		path: '/plan/subscribe/payment-successful',
		element: (
			<AppLayout>
				<PlanPaymentSuccess subscribe key={'PlanOnePaymentSuccess'} />
			</AppLayout>
		),
	},
	{
		path: '/seek-founders',
		element: (
			<AppLayout>
				<Founders key={'founders'} />
			</AppLayout>
		),
	},
	{
		path: '/sample-classes',
		element: (
			<AppLayout>
				<SampleClasses key={'founders'} />
			</AppLayout>
		),
	},
	{
		path: '/upcoming-sessions',
		element: (
			<AppLayout>
				<UpcomingSessions key={'UpcomingSessions'} />
			</AppLayout>
		),
	},
	{
		path: '/joining-instruction',
		element: (
			<AppLayout>
				<HowToJoinLiveClass key={'HowToJoinLiveClass'} />
			</AppLayout>
		),
	},
	{
		path: '/change-slots',
		element: (
			<AppLayout>
				<ChangeTimeSlotsPage key={'ChangeTimeSlotsPage'} />
			</AppLayout>
		),
	},
	{
		path: '/manage-reminders',
		element: (
			<AppLayout>
				<ManageReminders key={'ManageReminders'} />
			</AppLayout>
		),
	},
	{
		path: '/manage-notification',
		element: (
			<AppLayout>
				<ManageNotification key={'ManageReminders'} />
			</AppLayout>
		),
	},

	{
		path: '/live-streaming',
		element: (
			<AppLayout>
				<VimeoVideoPlayer key={'VimeoVideoPlayer'} />
			</AppLayout>
		),
	},
	{
		path: '/upcoming-course/:courseId',
		element: (
			<AppLayout>
				<UpcomingCourseDescriptionPage key={'UpcomingCourseDescriptionPage'} />
			</AppLayout>
		),
	},
	{
		path: '/upcoming-event/:eventId',
		element: (
			<AppLayout>
				<UpcomingEventDescriptionPage key={'UpcomingEventDescriptionPage'} />
			</AppLayout>
		),
	},
	{
		path: '/privacy-policy',
		element: (
			<AppLayout>
				<PrivacyPolicy key={'Policy'} />
			</AppLayout>
		),
	},
	{
		path: '/sai-meditation',
		element: (
			<AppLayout>
				<SaiServer key={'SaiServer'} />
			</AppLayout>
		),
	},
	{
		path: '/sai-v3',
		element: (
			<AppLayout>
				<SaiElevenLabsCall key={'SaiElevenLabsCall'} />
			</AppLayout>
		),
	},
	{
		path: '/sai-test',
		element: (
			<AppLayout>
				<SaiElevenLabsCallTest key={'SaiElevenLabsCallTest'} />
			</AppLayout>
		),
	},
	{
		path: '/sai-with-chat-v1',
		element: (
			<AppLayout>
				<SaiCallAndChat key={'SaiCallAndChat'} />
			</AppLayout>
		),
	},

	{
		path: '/sai-server',
		element: (
			<AppLayout>
				<SaiServer key={'SaiServer'} />
			</AppLayout>
		),
	},
	{
		path: '/terms-conditions',
		element: (
			<AppLayout>
				<TermsConditions key={'Terms&Conditions'} />
			</AppLayout>
		),
	},
	{
		path: '/support',
		element: (
			<AppLayout>
				<Support key={'Support'} />
			</AppLayout>
		),
	},
	{
		path: '/seek-community',
		element: (
			<AppLayout>
				<SeekCommunity key={'SeekCommunity'} />
			</AppLayout>
		),
	},
	// {
	// 	path: '/Kundalini-for-beginners/live-session',
	// 	element: (
	// 		<AppLayout>
	// 			<UpcomingCourseClass key={'UpcomingCourseClass'} />
	// 		</AppLayout>
	// 	),
	// },

	{
		path: '/:courseName/live-session',
		element: (
			<AppLayout>
				<UpcomingCourseClass key={'UpcomingCourseClass'} />
			</AppLayout>
		),
	},

	// {
	// 	path: '/Kundalini-for-beginners',
	// 	element: (
	// 		<AppLayout>
	// 			<CourseHomework key={'CourseHomework'} />
	// 		</AppLayout>
	// 	),
	// },

	{
		path: '/:courseName',
		element: (
			<AppLayout>
				<CourseHomework key={'CourseHomework'} />
			</AppLayout>
		),
	},
	{
		path: '/course/payment-successful',
		element: (
			<AppLayout>
				<CoursePaymentSuccessScreen key={'CoursePaymentSuccess'} />
			</AppLayout>
		),
	},
	{
		path: '/event/payment-successful',
		element: (
			<AppLayout>
				<EventPaymentSuccessScreen key={'EventPaymentSuccessScreen'} />
			</AppLayout>
		),
	},
	{
		path: '/plan/payment-successful',
		element: (
			<AppLayout>
				<PlanPaymentSuccess key={'PlanPaymentSuccess'} />
			</AppLayout>
		),
	},
	{
		path: '/favourite-classes',
		element: (
			<AppLayout>
				<FavouriteClasses key={'FavouriteClasses'} />
			</AppLayout>
		),
	},
]);
